import { useEffect, useState } from 'react';
import {
  Cloud, LayoutGrid, ListChecks, FileSpreadsheet, Presentation, Loader2, Info, Globe2, Gavel, LayoutList, Briefcase, Database, X, ShieldCheck,
} from 'lucide-react';
import { apiClient } from '../../../api';
import type { CloudSubView, FunnelKpis } from './types';
import FunnelDashboard from './FunnelDashboard';
import AppPortfolioGrid from './AppPortfolioGrid';
import CategoryTracker from './CategoryTracker';
import ImportBaselines from './ImportBaselines';
import ScopeSummary from './ScopeSummary';
import DispositionWorkbench from './DispositionWorkbench';
import ManagementConsole from './ManagementConsole';
import ExecutiveRollup from './ExecutiveRollup';
import ValidationLab from './ValidationLab';

const SUBS: { id: CloudSubView; label: string }[] = [
  { id: 'scope', label: 'Scope' },
  { id: 'workbench', label: 'Workbench' },
  { id: 'console', label: 'Console' },
  { id: 'executive', label: 'Executive' },
  { id: 'funnel', label: 'Funnel' },
  { id: 'portfolio', label: 'Portfolio' },
  { id: 'categories', label: 'Category Tracker' },
  { id: 'validation', label: 'Validation' },
];

export default function CloudPortfolioTab() {
  const [sub, setSub] = useState<CloudSubView>('funnel');
  const [visited, setVisited] = useState<Set<CloudSubView>>(() => new Set(['funnel']));
  const [dataOpen, setDataOpen] = useState(false);
  const [scope, setScope] = useState<string | null>(null);
  const [scopeLoaded, setScopeLoaded] = useState(false);
  const [basis, setBasis] = useState<string>('MIGRATION_SCOPE');

  useEffect(() => {
    let cancelled = false;
    apiClient.get('/api/cloud-portfolio/my-scope')
      .then((r) => { if (!cancelled) setScope(r.data?.scope || null); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setScopeLoaded(true); });
    return () => { cancelled = true; };
  }, []);
  const [funnel, setFunnel] = useState<FunnelKpis | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<'xlsx' | 'pptx' | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient.get('/api/cloud-portfolio/funnel')
      .then((r) => { if (!cancelled) { setFunnel(r.data); setError(null); } })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.error || e.message || 'Failed to load funnel'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tick]);

  const download = async (kind: 'xlsx' | 'pptx') => {
    setExporting(kind);
    try {
      const res = await apiClient.get(`/api/cloud-portfolio/export.${kind}`, {
        responseType: 'blob',
        params: { scope: scope || undefined, basis },
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = kind === 'xlsx' ? 'cloud-portfolio.xlsx' : 'cloud-portfolio-funnel.pptx';
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(null);
    }
  };

  const empty = !loading && (funnel?.assessed === 0 && funnel?.phase3.rows === 0 && funnel?.out_of_scope === 0);
  const extractAsOf = funnel?.universe?.as_of || null;
  const extractAge = extractAsOf ? Math.floor((Date.now() - new Date(extractAsOf).getTime()) / 86400000) : null;
  const extractStale = extractAge != null && extractAge > 14;
  const openData = () => setDataOpen(true);

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="px-5 pt-4 pb-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Cloud className="w-4 h-4 text-amber-500" />
          <span className="text-sm font-semibold text-super-dark-purple">Cloud Portfolio</span>
          {funnel?.as_of && (
            <span className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-full px-2.5 py-0.5">
              as of {funnel.as_of}
            </span>
          )}
          {extractAsOf && (
            <span className={`text-xs rounded-full px-2.5 py-0.5 border ${
              extractStale ? 'text-amber-800 bg-amber-50 border-amber-200 font-semibold' : 'text-gray-500 bg-gray-50 border-gray-200'
            }`}>
              extract {extractAsOf} · {extractAge === 0 ? 'today' : `${extractAge} day${extractAge === 1 ? '' : 's'} old`}{extractStale ? ' — stale' : ''}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={openData}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-gray-50 text-gray-600 rounded-lg text-xs font-semibold transition-colors border border-gray-200"
          >
            <Database className="w-3.5 h-3.5" />
            Data
          </button>
          <button
            onClick={() => download('xlsx')}
            disabled={!!exporting}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-gray-50 text-gray-600 rounded-lg text-xs font-semibold transition-colors border border-gray-200 disabled:opacity-50"
          >
            {exporting === 'xlsx' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />}
            Excel
          </button>
          <button
            onClick={() => download('pptx')}
            disabled={!!exporting}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-xs font-semibold transition-colors disabled:opacity-50"
          >
            {exporting === 'pptx' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Presentation className="w-3.5 h-3.5" />}
            Funnel PPT
          </button>
        </div>
      </div>

      <div className="px-5 flex gap-1 flex-wrap">
        {SUBS.map((t) => (
          <button
            key={t.id}
            onClick={() => { setSub(t.id); setVisited((v) => (v.has(t.id) ? v : new Set(v).add(t.id))); }}
            className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
              sub === t.id ? 'bg-amber-500 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {t.id === 'scope' && <Globe2 className="w-3.5 h-3.5" />}
            {t.id === 'workbench' && <Gavel className="w-3.5 h-3.5" />}
            {t.id === 'console' && <LayoutList className="w-3.5 h-3.5" />}
            {t.id === 'executive' && <Briefcase className="w-3.5 h-3.5" />}
            {t.id === 'funnel' && <LayoutGrid className="w-3.5 h-3.5" />}
            {t.id === 'portfolio' && <Cloud className="w-3.5 h-3.5" />}
            {t.id === 'categories' && <ListChecks className="w-3.5 h-3.5" />}
            {t.id === 'validation' && <ShieldCheck className="w-3.5 h-3.5" />}
            {t.label}
            {t.id === 'funnel' && funnel && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                sub === t.id ? 'bg-white/25 text-white' : 'bg-gray-200 text-gray-600'
              }`}>{funnel.assessed}</span>
            )}
            {t.id === 'categories' && funnel && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                sub === t.id ? 'bg-white/25 text-white' : 'bg-gray-200 text-gray-600'
              }`}>{funnel.phase3.rows}</span>
            )}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        {error && (
          <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
            <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            {error}
          </div>
        )}
        {loading && <p className="text-xs text-gray-500">Loading portfolio…</p>}
        {!loading && empty && sub !== 'scope' && sub !== 'workbench' && sub !== 'console' && sub !== 'executive' && (
          <div className="flex items-start gap-2 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
            <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            No Cloud Portfolio rows yet. Import Phase 2 (`Final Application data.xlsx`) and Phase 3 (`Cloud Migration Application Tracker`) from the Data menu. Hidden Cat 1–3 tracker rows are included on ingest.
          </div>
        )}
        {visited.has('scope') && (
          <div hidden={sub !== 'scope'}><ScopeSummary refreshKey={tick} scope={scope} onScopeChange={setScope} basis={basis} onBasisChange={setBasis} onOpenImports={openData} onOpenWorkbench={() => { setSub('workbench'); setVisited((v) => (v.has('workbench') ? v : new Set(v).add('workbench'))); }} onConfigChanged={() => setTick((n) => n + 1)} /></div>
        )}
        {visited.has('workbench') && (
          <div hidden={sub !== 'workbench'}><DispositionWorkbench refreshKey={tick} scope={scope} scopeLoaded={scopeLoaded} basis={basis} /></div>
        )}
        {visited.has('console') && (
          <div hidden={sub !== 'console'}><ManagementConsole refreshKey={tick} scope={scope} scopeLoaded={scopeLoaded} basis={basis} /></div>
        )}
        {visited.has('executive') && (
          <div hidden={sub !== 'executive'}><ExecutiveRollup refreshKey={tick} scope={scope} scopeLoaded={scopeLoaded} basis={basis} onScopeChange={setScope} /></div>
        )}
        {sub === 'funnel' && funnel && <FunnelDashboard funnel={funnel} onOpenImports={openData} />}
        {visited.has('portfolio') && (
          <div hidden={sub !== 'portfolio'}><AppPortfolioGrid refreshKey={tick} /></div>
        )}
        {visited.has('categories') && (
          <div hidden={sub !== 'categories'}><CategoryTracker refreshKey={tick} /></div>
        )}
        {visited.has('validation') && (
          <div hidden={sub !== 'validation'}><ValidationLab refreshKey={tick} /></div>
        )}
      </div>

      {dataOpen && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={() => setDataOpen(false)}>
          <div
            className="w-full max-w-3xl h-full bg-white shadow-xl overflow-y-auto p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Database className="w-4 h-4 text-amber-500" />
                <span className="text-sm font-semibold text-super-dark-purple">Data — imports & baselines</span>
              </div>
              <button onClick={() => setDataOpen(false)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
                <X className="w-4 h-4" />
              </button>
            </div>
            <ImportBaselines onImported={() => setTick((n) => n + 1)} />
          </div>
        </div>
      )}
    </div>
  );
}
